import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, RefreshCw, Trophy, User, Cpu, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ------------------------------------------------------------------------
// Constants & Configuration
// ------------------------------------------------------------------------

const CELL_SIZE = 22; // Pixel size for spacing
// We'll use a virtual grid where rows are 0..16
// and columns are 0..24 (approximately).
// Even rows have even col indices, Odd rows have odd col indices (checkerboard).

// Valid Board Coordinates Generation
const generateValidPoints = () => {
    const points = [];
    // Helper to add range
    const addRange = (row, startCol, endCol) => {
        for (let c = startCol; c <= endCol; c += 2) {
            points.push({ r: row, c: c });
        }
    };

    // Top Triangle (P1)
    addRange(0, 12, 12);
    addRange(1, 11, 13);
    addRange(2, 10, 14);
    addRange(3, 9, 15);

    // Upper Middle (P6 Left, Center, P2 Right)
    addRange(4, 0, 24);
    addRange(5, 1, 23);
    addRange(6, 2, 22);
    addRange(7, 3, 21);

    // Center Line
    addRange(8, 4, 20);

    // Lower Middle (P5 Left, Center, P3 Right)
    addRange(9, 3, 21);
    addRange(10, 2, 22);
    addRange(11, 1, 23);
    addRange(12, 0, 24);

    // Bottom Triangle (P4)
    addRange(13, 9, 15);
    addRange(14, 10, 14);
    addRange(15, 11, 13);
    addRange(16, 12, 12);

    return points;
};
// ... (ZONES, TARGETS, PLAYER_COLORS, ChineseCheckersGame etc.)
// ... in ChineseCheckersGame component:
//                                    <div 
//                                        className="relative"
//                                        style={{ 
//                                            width: `${25 * CELL_SIZE}px`, 
//                                            height: `${17 * CELL_SIZE * 1.75}px` 
//                                        }}
//                                    >
//                                        {VALID_POINTS.map((p, i) => {
//                                            const pid = getPiece(board, p.r, p.c);
//                                            const isSelected = selectedPiece && selectedPiece.r === p.r && selectedPiece.c === p.c;
//                                            const isPossible = possibleMoves.some(m => m.r === p.r && m.c === p.c);
//                                            
//                                            // Calculate Position
//                                            // Row Height ~ line height.
//                                            // Col Width ~ half cell size (staggered).
//                                            // Equilateral triangle height is side * sqrt(3)/2
//                                            // Here "side" is 2 * CELL_SIZE (horizontal step is 2) 
//                                            // Wait, neighbor distance is 2*CELL_SIZE? No.
//                                            // In our grid c steps by 2.
//                                            // Visual x = c * CELL_SIZE.
//                                            // Visual neighbor dist = 2 * CELL_SIZE.
//                                            // So side length S = 2 * CELL_SIZE.
//                                            // Row height should be S * sqrt(3) / 2 = CELL_SIZE * sqrt(3)
//                                            const x = p.c * CELL_SIZE; 
//                                            const y = p.r * (CELL_SIZE * Math.sqrt(3));


const VALID_POINTS = generateValidPoints();
const VALID_POINTS_SET = new Set(VALID_POINTS.map(p => `${p.r},${p.c}`));

// Zones Definition (for Setup and Win conditions)
// Format: color/ID maps to set of points
const ZONES = {
    1: VALID_POINTS.filter(p => p.r <= 3), // Top (Green)
    4: VALID_POINTS.filter(p => p.r >= 13), // Bottom (Red)
    6: VALID_POINTS.filter(p => p.r >= 4 && p.r <= 7 && p.c <= (4 + (p.r-4))), // Top Left (Orange)  Wait, verifying logic:
       // Row 4: 0, 2, 4, 6 (4 items). End is index 6. 
       // Row 5: 1, 3, 5. End is 5.
       // Row 6: 2, 4. End is 4.
       // Row 7: 3. End is 3.
    // Let's refine logical filters based on the specific points
    // P6 (TL) Logic:
    // Row 4: c <= 6
    // Row 5: c <= 5
    // Row 6: c <= 4
    // Row 7: c <= 3
    
    // P2 (TR) Logic:
    // Row 4: c >= 18
    // Row 5: c >= 19
    // Row 6: c >= 20
    // Row 7: c >= 21
    
    // P5 (BL) Logic:
    // Row 9: c <= 3
    // Row 10: c <= 4
    // Row 11: c <= 5
    // Row 12: c <= 6
    
    // P3 (BR) Logic:
    // Row 9: c >= 21
    // Row 10: c >= 20
    // Row 11: c >= 19
    // Row 12: c >= 18
};

// Refine ZONES with explicit filters
ZONES[6] = VALID_POINTS.filter(p => (p.r===4 && p.c<=6) || (p.r===5 && p.c<=5) || (p.r===6 && p.c<=4) || (p.r===7 && p.c<=3));
ZONES[2] = VALID_POINTS.filter(p => (p.r===4 && p.c>=18) || (p.r===5 && p.c>=19) || (p.r===6 && p.c>=20) || (p.r===7 && p.c>=21));
ZONES[5] = VALID_POINTS.filter(p => (p.r===9 && p.c<=3) || (p.r===10 && p.c<=4) || (p.r===11 && p.c<=5) || (p.r===12 && p.c<=6));
ZONES[3] = VALID_POINTS.filter(p => (p.r===9 && p.c>=21) || (p.r===10 && p.c>=20) || (p.r===11 && p.c>=19) || (p.r===12 && p.c>=18));

// Player Configs
// Colors: 1:Green, 2:Yellow, 3:Purple, 4:Red, 5:Blue, 6:Orange
// TARGETS maps Start Zone ID -> Target Zone ID
const TARGETS = { 1: 4, 4: 1, 2: 5, 5: 2, 3: 6, 6: 3 };

const PLAYER_COLORS = {
    1: 'bg-green-500', 4: 'bg-red-500',
    2: 'bg-yellow-400', 5: 'bg-blue-500',
    3: 'bg-purple-500', 6: 'bg-orange-500'
};

const DEFAULT_PLAYER_NAMES = {
    1: 'Green', 4: 'Red',
    2: 'Yellow', 5: 'Blue',
    3: 'Purple', 6: 'Orange'
};

const ChineseCheckersGame = () => {
    const navigate = useNavigate();

    // ------------------------------------------------------------------------
    // State
    // ------------------------------------------------------------------------
    
    const [gameMode, setGameMode] = useState('PvC'); // PvC, PvP
    const [playerCount, setPlayerCount] = useState(2); // 2, 3, 4, 6
    const [gameState, setGameState] = useState('SETUP'); // SETUP, PLAYING, ENDED
    const [difficulty, setDifficulty] = useState('Easy'); // Easy, Medium, Hard
    
    // Player Names State
    const [playerNames, setPlayerNames] = useState(DEFAULT_PLAYER_NAMES);

    // Board State: string key "r,c" -> player ID (number)
    const [board, setBoard] = useState({});
    
    // Turn State
    const [turnIndex, setTurnIndex] = useState(0); // Index in activePlayers array
    const [activePlayers, setActivePlayers] = useState([]); // Array of player IDs, ordered
    
    // Selection & Moves
    const [selectedPiece, setSelectedPiece] = useState(null); // {r, c}
    const [possibleMoves, setPossibleMoves] = useState([]); // Array of {r, c}
    
    // Winner
    const [winner, setWinner] = useState(null);

    // ------------------------------------------------------------------------
    // Game Logic Helpers
    // ------------------------------------------------------------------------
    // Helper to get active player IDs before game starts for UI preview
    const getActivePlayerIds = (cnt, mode) => {
        let players = [];
        if (cnt === 2) players = [4, 1];
        else if (cnt === 3) players = [1, 3, 5];
        else if (cnt === 4) players = [6, 2, 3, 5];
        else if (cnt === 6) players = [1, 2, 3, 4, 5, 6];
        
        if (cnt === 2 && mode === 'PvC') {
             // 4 is User, 1 is Bot
             return [4, 1];
        }
        return players.sort((a,b) => a - b);
    };

    const isValidPoint = (r, c) => VALID_POINTS_SET.has(`${r},${c}`);
    const getPiece = (b, r, c) => b[`${r},${c}`];


    // Directions: 6 neighbors in axial hex grid
    // For our staggered grid:
    // Left/Right: (0, -2), (0, 2)
    // Top-Left: (-1, -1), Top-Right: (-1, 1)
    // Bottom-Left: (1, -1), Bottom-Right: (1, 1)
    const DIRECTIONS = [
        {dr: 0, dc: -2}, {dr: 0, dc: 2},
        {dr: -1, dc: -1}, {dr: -1, dc: 1},
        {dr: 1, dc: -1}, {dr: 1, dc: 1}
    ];

    const getNeighbors = (r, c) => {
        return DIRECTIONS.map(d => ({ r: r + d.dr, c: c + d.dc }))
            .filter(p => isValidPoint(p.r, p.c));
    };

    const initializeGame = () => {
        const newBoard = {};
        
        const players = getActivePlayerIds(playerCount, gameMode);

        // Setup Pieces
        players.forEach(pid => {
            ZONES[pid].forEach(p => {
                newBoard[`${p.r},${p.c}`] = pid;
            });
        });

        setBoard(newBoard);
        setActivePlayers(players);
        setTurnIndex(0);
        setWinner(null);
        setGameState('PLAYING');
        setSelectedPiece(null);
        setPossibleMoves([]);
    };

    // Move Calculation
    const getValidMoves = (b, r, c) => {
        const moves = new Set();
        const visited = new Set();
        const queue = [];

        // 1. Single Steps
        getNeighbors(r, c).forEach(n => {
            if (!getPiece(b, n.r, n.c)) {
                moves.add(`${n.r},${n.c}`);
            }
        });

        // 2. Jumps (Recursive/BFS)
        // A jump is valid if neighbor has piece AND spot beyond is empty
        const pushJump = (currR, currC) => {
             getNeighbors(currR, currC).forEach(n => {
                if (getPiece(b, n.r, n.c)) {
                    // Potential jump over 'n'
                    // Calculate landing spot
                    const dr = n.r - currR;
                    const dc = n.c - currC;
                    const landR = n.r + dr;
                    const landC = n.c + dc;
                    
                    if (isValidPoint(landR, landC) && !getPiece(b, landR, landC)) {
                        const key = `${landR},${landC}`;
                        if (!visited.has(key) && !(landR === r && landC === c)) { // Don't jump back to start in loop immediately (allow cycles but track visited)
                            visited.add(key);
                            moves.add(key); // Landing spot is valid move
                            queue.push({r: landR, c: landC}); // Continue jumping from here
                        }
                    }
                }
             });
        };

        // Start BFS for jumps
        // Note: Can we combine single step and jump step? 
        // Rules say: "A move consists of ONE single step OR a chain of hops".
        // You cannot step then hop, or hop then step (except as start of new chain? No).
        // So we just add all hops to the moves set.
        
        // Initial hops from start
        pushJump(r, c);
        
        // Process queue
        while(queue.length > 0) {
            const curr = queue.shift();
            pushJump(curr.r, curr.c);
        }

        return Array.from(moves).map(k => {
            const [mr, mc] = k.split(',').map(Number);
            return {r: mr, c: mc};
        });
    };

    const handleCellClick = (r, c) => {
        if (gameState !== 'PLAYING') return;
        
        const currentPlayer = activePlayers[turnIndex];
        
        // If PvC and it's Bot's turn, ignore
        if (gameMode === 'PvC' && currentPlayer !== 4) return;
        
        const piece = getPiece(board, r, c);

        // Select own piece
        if (piece === currentPlayer) {
            setSelectedPiece({r, c});
            setPossibleMoves(getValidMoves(board, r, c));
            return;
        }

        // Move to empty spot if valid
        if (selectedPiece && !piece) {
            // Check if move is in possibleMoves
            if (possibleMoves.some(m => m.r === r && m.c === c)) {
                executeMove(selectedPiece, {r, c});
            } else {
                // Clicked invalid empty spot, deselect
                setSelectedPiece(null);
                setPossibleMoves([]);
            }
        }
    };

    const executeMove = (from, to) => {
        const currentPlayer = activePlayers[turnIndex];
        const newBoard = { ...board };
        
        delete newBoard[`${from.r},${from.c}`];
        newBoard[`${to.r},${to.c}`] = currentPlayer;
        
        setBoard(newBoard);
        setSelectedPiece(null);
        setPossibleMoves([]);
        
        checkWin(newBoard, currentPlayer); // Check win before passing turn
        nextTurn();
    };

    const nextTurn = () => {
        setTurnIndex(prev => (prev + 1) % activePlayers.length);
    };
    
    // Win Condition
    const checkWin = (currentBoard, pid) => {
        const targetZoneId = TARGETS[pid];
        const targetPoints = ZONES[targetZoneId];
        
        // Check if ALL target points are occupied by PID
        const allInObj = targetPoints.every(p => currentBoard[`${p.r},${p.c}`] === pid);
        
        if (allInObj) {
            setGameState('ENDED');
            setWinner(pid);
        }
    };

    // ------------------------------------------------------------------------
    // AI / Bot Logic
    // ------------------------------------------------------------------------
    useEffect(() => {
        if (gameState === 'PLAYING' && gameMode === 'PvC') {
            const currentPlayer = activePlayers[turnIndex];
            if (currentPlayer !== 4) { // 4 is User
                // Computer Turn
                const timer = setTimeout(() => {
                    performBotMove(currentPlayer);
                }, 700);
                return () => clearTimeout(timer);
            }
        }
    }, [turnIndex, gameState, gameMode, activePlayers]); // Add missing deps

    const performBotMove = (botId) => {
        // Simple Greedy AI
        // 1. Find all pieces
        const pieces = [];
        Object.entries(board).forEach(([k, v]) => {
            if (v === botId) {
                const [r, c] = k.split(',').map(Number);
                pieces.push({r, c});
            }
        });

        // 2. Evaluate all moves
        // Target: Opposite zone.
        // Target Center Point for heuristic:
        // P1 (Top) -> Target P4 (Bottom, r=16, c=12)
        // P4 -> P1 (r=0, c=12)
        // etc.
        const targetCenter = getTargetCenter(botId);
        
        let bestMove = null;
        let bestScore = -Infinity;

        // Add randomness for 'Easy'
        const isEasy = difficulty === 'Easy';
        
        pieces.forEach(p => {
            const moves = getValidMoves(board, p.r, p.c);
            moves.forEach(m => {
                // Score: Distance reduced to target
                const currentDist = getDistance(p, targetCenter);
                const newDist = getDistance(m, targetCenter);
                let score = currentDist - newDist; // Positive is good (got closer)
                
                // Bonus for hops (advancing further)? 
                // Indirectly captured by distance.
                
                // Strategy: Prioritize trailing pieces?
                // For now, raw distance.
                
                if (isEasy) {
                    score += (Math.random() - 0.5) * 5; // Noise
                }

                if (score > bestScore) {
                    bestScore = score;
                    bestMove = { from: p, to: m };
                }
            });
        });

        if (bestMove) {
            executeMove(bestMove.from, bestMove.to);
        } else {
            // No moves? Pass
            nextTurn();
        }
    };
    
    const getTargetCenter = (pid) => {
        const tid = TARGETS[pid];
        if (tid === 1) return {r:0, c:12};
        if (tid === 4) return {r:16, c:12};
        if (tid === 2) return {r:4, c:24};
        if (tid === 3) return {r:12, c:24};
        if (tid === 5) return {r:12, c:0};
        if (tid === 6) return {r:4, c:0};
        return {r:8, c:12};
    };
    
    const getDistance = (p1, p2) => {
        // Hex Manhattan distance approximation or Euclidean
        // On this skewed grid, Euclidean is okay for heuristic
        return Math.sqrt(Math.pow(p1.r - p2.r, 2) + Math.pow((p1.c - p2.c)/2, 2)); // Divide col by 2 to normalize aspect
    };

    // ------------------------------------------------------------------------
    // UI Rendering
    // ------------------------------------------------------------------------

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={() => navigate('/games')}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium mb-6"
                >
                    <ArrowLeft className="w-5 h-5 mr-1" />
                    Back to Games
                </button>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="bg-indigo-600 p-6 text-white text-center">
                        <h1 className="text-3xl font-bold mb-2">Chinese Checkers</h1>
                        <p className="text-indigo-100">Race your marbles to the opposite star point!</p>
                    </div>

                    <div className="p-8">
                        {gameState === 'SETUP' ? (
                            <div className="max-w-md mx-auto space-y-8">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Game Mode</label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            onClick={() => { setGameMode('PvC'); setPlayerCount(2); }}
                                            className={`p-4 rounded-lg border-2 flex flex-col items-center ${gameMode === 'PvC' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'}`}
                                        >
                                            <Cpu className="w-8 h-8 mb-2" />
                                            <span className="font-bold">Vs Computer</span>
                                        </button>
                                        <button
                                            onClick={() => setGameMode('PvP')}
                                            className={`p-4 rounded-lg border-2 flex flex-col items-center ${gameMode === 'PvP' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'}`}
                                        >
                                            <Users className="w-8 h-8 mb-2" />
                                            <span className="font-bold">Multiplayer</span>
                                        </button>
                                    </div>
                                </div>

                                {gameMode === 'PvP' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Players</label>
                                        <div className="flex justify-between gap-2">
                                            {[2, 3, 4, 6].map(n => (
                                                <button
                                                    key={n}
                                                    onClick={() => setPlayerCount(n)}
                                                    className={`flex-1 py-2 rounded-md font-medium border ${playerCount === n ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Player Names Inputs */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Player Names</label>
                                    <div className="space-y-3">
                                        {getActivePlayerIds(playerCount, gameMode).map(pid => {
                                             // For PvC, we only rename the User (ID 4)
                                             const isBot = gameMode === 'PvC' && pid === 1;
                                             
                                             return (
                                                <div key={pid} className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 rounded-full ${PLAYER_COLORS[pid]} flex-shrink-0`} />
                                                    <input
                                                        type="text"
                                                        value={isBot ? "Computer" : playerNames[pid]}
                                                        disabled={isBot}
                                                        onChange={(e) => setPlayerNames(prev => ({...prev, [pid]: e.target.value}))}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                        placeholder={`Player ${pid}`}
                                                    />
                                                </div>
                                             );
                                        })}
                                    </div>
                                </div>


                                {gameMode === 'PvC' && (
                                     <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                                        <div className="flex gap-2">
                                            {['Easy', 'Medium', 'Hard'].map(d => (
                                                <button
                                                    key={d}
                                                    onClick={() => setDifficulty(d)}
                                                    className={`flex-1 py-2 rounded-md font-medium border ${difficulty === d ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                                >
                                                    {d}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={initializeGame}
                                    className="w-full py-4 bg-green-600 text-white rounded-lg text-xl font-bold hover:bg-green-700 transition-colors shadow-lg"
                                >
                                    Start Game
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                {/* HUD */}
                                <div className="flex items-center justify-between w-full max-w-2xl mb-6 bg-gray-100 p-4 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-gray-500 font-medium">Turn:</span>
                                        <div className={`px-3 py-1 rounded-full text-white font-bold flex items-center gap-2 ${PLAYER_COLORS[activePlayers[turnIndex]]}`}>
                                            <User className="w-4 h-4" />
                                            {playerNames[activePlayers[turnIndex]]}
                                        </div>
                                    </div>
                                    
                                    {gameMode === 'PvC' && (
                                         <div className="text-sm font-medium text-gray-500">
                                            Difficulty: <span className="text-gray-900">{difficulty}</span>
                                         </div>
                                    )}

                                    <button
                                        onClick={() => setGameState('SETUP')}
                                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full"
                                        title="Restart"
                                    >
                                        <RefreshCw className="w-5 h-5" />
                                    </button>
                                </div>

                                {gameState === 'ENDED' && (
                                    <div className="mb-6 p-4 bg-yellow-100 border-2 border-yellow-400 text-yellow-800 rounded-lg flex items-center animate-bounce">
                                        <Trophy className="w-6 h-6 mr-2" />
                                        <span className="font-bold text-lg">{playerNames[winner]} Wins!</span>
                                    </div>
                                )}


                                {/* BOARD - Scrollable Wrapper for Mobile */}
                                <div className="w-full overflow-x-auto pb-8">
                                    <div className="relative bg-amber-100 rounded-full p-8 shadow-[inset_0_2px_10px_rgba(0,0,0,0.1)] border-8 border-amber-900/10 min-w-fit mx-auto">
                                        <div 
                                            className="relative mx-auto"
                                            style={{ 
                                                width: `${25 * CELL_SIZE}px`, 
                                                height: `${17 * CELL_SIZE * 1.74}px` 
                                            }}
                                        >
                                            {VALID_POINTS.map((p, i) => {
                                            const pid = getPiece(board, p.r, p.c);
                                            const isSelected = selectedPiece && selectedPiece.r === p.r && selectedPiece.c === p.c;
                                            const isPossible = possibleMoves.some(m => m.r === p.r && m.c === p.c);
                                            
                                            // Calculate Position
                                            // x based on column (step 1 = CELL_SIZE)
                                            // y based on row. For equilateral triangles, row height = sqrt(3) * horizontal_unit_step?
                                            // Horizontal dist between (0,0) and (0,2) is 2*CELL_SIZE.
                                            // Distance to (1,1) should be 2*CELL_SIZE.
                                            // (1*CELL_SIZE)^2 + h^2 = (2*CELL_SIZE)^2
                                            // h^2 = 3 * CELL_SIZE^2 => h = CELL_SIZE * sqrt(3)
                                            const x = p.c * CELL_SIZE; 
                                            const y = p.r * (CELL_SIZE * 1.732);
                                            
                                            return (
                                                <div
                                                    key={`${p.r}-${p.c}`}
                                                    onClick={() => handleCellClick(p.r, p.c)}
                                                    className={`
                                                        absolute rounded-full flex items-center justify-center
                                                        transition-all duration-200 cursor-pointer
                                                        ${pid ? 'shadow-md z-10' : 'z-0'}
                                                        ${isSelected ? 'ring-4 ring-white scale-110' : ''}
                                                    `}
                                                    style={{
                                                        left: x,
                                                        top: y,
                                                        width: CELL_SIZE * 1.6,
                                                        height: CELL_SIZE * 1.6,
                                                        backgroundColor: pid ? undefined : 'rgba(0,0,0,0.1)', // Empty hole
                                                    }}
                                                >
                                                    {/* Marble */}
                                                    {pid && (
                                                        <div className={`w-full h-full rounded-full ${PLAYER_COLORS[pid]} shadow-[inset_-2px_-2px_6px_rgba(0,0,0,0.3),2px_2px_6px_rgba(255,255,255,0.3)]`}>
                                                            {/* Shine */}
                                                            <div className="absolute top-1/4 left-1/4 w-1/3 h-1/3 bg-white opacity-40 rounded-full filter blur-[1px]" />
                                                        </div>
                                                    )}
                                                    
                                                    {/* Possible Move Highlight */}
                                                    {isPossible && (
                                                        <div className="absolute inset-0 bg-green-500 opacity-50 rounded-full animate-pulse transform scale-75" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* End of Scrollable Wrapper */}
                                </div>
                                
                                <p className="mt-8 text-gray-500 text-sm max-w-lg text-center">
                                    Move all your marbles to the opposite triangle. You can jump over other pieces to move faster!
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChineseCheckersGame;
